import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

/**
 * ConsentBanner — GDPR-compliant Consent Mode v2 gate.
 *
 * How it fits with the tag setup:
 *   - public/index.html loads gtag.js with a `gtag('consent','default')`
 *     block that denies all storage flags upfront. Google Ads + GA4
 *     still fire modeled/anonymous signal but do NOT set cookies or
 *     identifiers.
 *   - This banner asks the visitor to explicitly accept or reject.
 *   - Accept → gtag('consent','update', {...'granted'}) and persist
 *     "granted" in localStorage under `saturn_cookie_consent`.
 *   - Reject → persist "denied" so we don't re-prompt. Google keeps
 *     receiving denied-mode signal; that's intentional (see Consent
 *     Mode v2 docs).
 *   - The initial inline script in index.html reads the localStorage
 *     value on every pageload and re-applies the update BEFORE any tag
 *     config fires, so returning accepted visitors are not counted as
 *     denied on their first pageview.
 *
 * Design language: matches the promo banner + footer utility links —
 * dark slate background, orange accent for the primary action, muted
 * slate for the secondary "Reject". Fixed to the bottom, non-modal
 * (doesn't block interaction with the site — good UX + not required
 * by GDPR to be blocking).
 */

const STORAGE_KEY = "saturn_cookie_consent";

const _updateGtagConsent = (granted) => {
  if (typeof window === "undefined" || !window.gtag) return;
  const state = granted ? "granted" : "denied";
  window.gtag("consent", "update", {
    ad_storage: state,
    ad_user_data: state,
    ad_personalization: state,
    analytics_storage: state,
  });
};

/**
 * Mirror the Google consent update onto Meta's Pixel. Meta's consent
 * API is a two-state grant/revoke rather than the four-flag matrix
 * Google uses, but the semantics line up: grant on Accept, revoke on
 * Reject. See public/index.html for the base-pixel setup.
 *
 * On grant we ALSO fire an explicit PageView. Reason: the inline
 * bootstrap in index.html only fires PageView for visitors whose
 * localStorage already says 'granted' at page load. For a first-time
 * visitor who accepts via this banner, the initial PageView was NEVER
 * queued (we called revoke without a track) — so we fire it here so
 * Meta records at least one PageView per accepted session, otherwise
 * Purchase would fire without any prior PageView which breaks Meta's
 * attribution model.
 *
 * Guard for the case where the Pixel script hasn't finished loading
 * yet: calls queue against `window.fbq.queue` and are flushed once
 * fbevents.js finishes booting. Since these run AFTER a real user
 * click, fbevents.js has almost certainly loaded already.
 */
const _updateMetaConsent = (granted) => {
  if (typeof window === "undefined" || typeof window.fbq !== "function") return;
  if (granted) {
    window.fbq("consent", "grant");
    window.fbq("track", "PageView");
  } else {
    window.fbq("consent", "revoke");
  }
};

const ConsentBanner = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Delay initial render slightly so we don't compete with above-the-
    // fold hero animations — feels less jarring than a same-frame slide-in.
    let raf;
    try {
      const existing = localStorage.getItem(STORAGE_KEY);
      if (existing === "granted" || existing === "denied") {
        // Already decided. index.html's inline consent-restore has
        // already re-applied "granted" for the gtag flags if applicable,
        // so nothing else to do here.
        return;
      }
      raf = window.setTimeout(() => setVisible(true), 600);
    } catch (e) {
      // localStorage disabled/blocked (privacy browsers) — show the
      // banner every visit; without storage we can't remember the
      // choice anyway, and the visitor is clearly privacy-inclined.
      raf = window.setTimeout(() => setVisible(true), 600);
    }
    return () => raf && window.clearTimeout(raf);
  }, []);

  const handleAccept = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "granted");
    } catch (e) { /* privacy browsers — banner will re-appear next visit */ }
    _updateGtagConsent(true);
    _updateMetaConsent(true);
    setVisible(false);
  };

  const handleReject = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "denied");
    } catch (e) { /* same as above */ }
    _updateGtagConsent(false);
    _updateMetaConsent(false);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed bottom-0 left-0 right-0 z-[70] px-4 pb-4 sm:pb-6 pointer-events-none"
      data-testid="cookie-consent-banner"
    >
      <div
        className="mx-auto max-w-4xl pointer-events-auto rounded-xl border border-slate-700/70 bg-slate-950/95 backdrop-blur-md shadow-2xl shadow-black/50 px-4 py-4 sm:px-6 sm:py-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6"
      >
        <div className="flex-1 text-slate-300 text-sm leading-relaxed font-inter">
          We use cookies to see how visitors use our site and which ads
          lead to real bookings, so we can keep improving your
          experience. Essential cookies are always on — you choose
          whether to allow analytics and advertising cookies. See our{" "}
          <Link
            to="/privacy"
            className="text-saturn-orange underline underline-offset-2 hover:text-saturn-orange/80"
            data-testid="cookie-consent-privacy-link"
          >
            Privacy Policy
          </Link>{" "}
          for details.
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={handleReject}
            className="px-3 py-2 text-sm text-slate-400 hover:text-slate-200 font-rajdhani rounded-md transition-colors"
            data-testid="cookie-consent-reject"
          >
            Reject Non-Essential
          </button>
          <button
            type="button"
            onClick={handleAccept}
            className="px-5 py-2 text-sm font-semibold text-slate-950 bg-saturn-orange hover:bg-saturn-orange/90 rounded-md transition-colors font-rajdhani"
            data-testid="cookie-consent-accept"
          >
            Accept All
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConsentBanner;
